/**
 * ================================================================================================
 *  גבייה — who has not paid, which stage they are in, and the day counts that decide it
 * ================================================================================================
 *
 * The Owner, 2026-11-02, authorising the automatic half:
 *
 *     *"התקדמות אוטומטית בשלבי הגבייה — כן. המערכת תתקדם לפי הזמנים שהוגדרו ותשלח את ההתראות.
 *      אין צורך באישור אנושי לכל מעבר. אין בכך אישור למחיקת נתונים."*
 *
 * And his §10, which is what the day counts mean:
 *
 *     *"כשל בחיוב יטופל בשלושה שלבים: התראה תוך המשך עבודה רגילה; לאחר 14 יום, הסדרה בלבד;
 *      לאחר 30 יום, הרשמה מחדש בלי שחזור אוטומטי ובלי ניסיון חדש אוטומטי."*
 *
 * ── THIS SCREEN WATCHES A PASS; IT IS NOT HOW THE PASS HAPPENS ─────────────────────────────────
 *
 * The nightly `dunning_sweep` advances every stage and sends every notice at 05:40 with nobody
 * signed in. So the table here is not a worklist an operator has to clear — it is what the pass
 * will do tonight, or did this morning. The one button per row exists for the case the Owner's
 * instruction does not cover: acting NOW rather than waiting for the next pass.
 *
 * ── THE STAGE COMES FROM THE SERVER, AND THIS FILE DOES NO ARITHMETIC ──────────────────────────
 *
 * `platform.dunning.read` computes `stage`, `daysUnsettled` and `demands` with the same two
 * functions the pass acts on. A screen that received the day count and compared it against
 * thresholds of its own would be a second copy of the policy — and the copy is always the one that
 * drifts, so it would show `warning` beside an office the pass had already restricted.
 *
 * The thresholds ARE shown, read from the same answer, because an operator looking at "19 days"
 * needs to know what 19 crosses. They are displayed, never applied here.
 *
 * ── NO GOVERNING POLICY IS A STATE THIS SCREEN NAMES ───────────────────────────────────────────
 *
 * `policy === null` means no dated row is in force today. In that state the pass refuses every
 * office with `no_governing_policy` and nothing advances — an estate with no arrears policy is not
 * an estate with no arrears. The screen says so at the top instead of rendering a table that looks
 * calm.
 */

import { useCallback, useState } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { InfoNote } from '../components/InfoNote';
import { Modal } from '../components/Modal';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import type {
  ArrearsLine,
  ArrearsReport,
  DunningPolicyReport,
  DunningReviewRead,
  DunningStage,
} from '../api/contract';
import { paymentStageHe } from '../api/billingText';
import { useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'גבייה';

type Outcome =
  | { readonly kind: 'reviewed'; readonly tenantUid: string; readonly review: DunningReviewRead }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PlatformDunningScreen(): JSX.Element {
  const ctx = useWorkContext();
  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }
  return <PlatformDunningBody />;
}

function PlatformDunningBody(): JSX.Element {
  const read = useCallback(() => callOperation('platform.dunning.read', {}), []);
  const arrears = useServerData(read);
  /*
   * A SECOND READ FOR THE DATED LIST. `platform.dunning.read` already answers which policy is in
   * force today -- that is what the stage was computed with, and the table needs it -- but it does
   * NOT carry the history, and the editor has to show it: a change is a new row dated forward, so
   * an operator setting one needs to see what is already dated and from when, or they will write a
   * second row for a day that has one and be refused.
   *
   * ITS OWN `<ServerRead>`, so a failure here does not hide the arrears table and a failure there
   * does not hide the thresholds.
   */
  const readPolicy = useCallback(() => callOperation('platform.dunning.policy.read', {}), []);
  const policies = useServerData(readPolicy);

  const [reviewing, setReviewing] = useState<ArrearsLine | null>(null);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const back = { labelHe: 'מרכז החיוב', href: routes.platformCharges };

  async function review(line: ArrearsLine): Promise<void> {
    setSending(true);
    try {
      /*
       * A WRITE, AND NEVER RETRIED. It can move a subscription state and send a notice, and the
       * client cannot know whether the server committed. A repeat would send a second notice to a
       * customer who has already had one — `noticeDueOn` refuses that inside the policy interval,
       * so the harm is bounded, and a press nobody made is still a press nobody made.
       */
      const result = await callOperation('platform.dunning.review', {
        body: { tenantUid: line.tenantUid },
      });
      setOutcome({ kind: 'reviewed', tenantUid: line.tenantUid, review: result });
      void arrears.reload();
    } catch (err) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      setReviewing(null);
    }
  }

  return (
    <AppShell titleHe={TITLE_HE} backToParent={back}>
      <InfoNote summaryHe="מי מטפל בשלבי הגבייה, ומה המסך הזה עושה">
        <p>
          השלבים מתקדמים מעצמם. סריקה יומית עוברת על כל חוב שלא הוסדר, מעבירה את המנוי למצב שהשלב
          דורש ושולחת את ההתראה — בלי שאדם יאשר כל מעבר.
        </p>
        <p>
          הטבלה מראה את מה שהסריקה תעשה הלילה, או עשתה הבוקר. השלב והמניין מחושבים בשרת, באותן
          פונקציות שהסריקה פועלת לפיהן, כדי שלא ייווצרו שני חישובים שונים לאותו חוב.
        </p>
        <p>
          הכפתור שליד שורה אינו חלק מההתקדמות האוטומטית: הוא מריץ את אותה בדיקה עכשיו, במקום להמתין
          לסריקה הבאה. הוא מעשה של אדם, ולכן נרשם בשמו.
        </p>
        <p>
          הסריקה מעבירה מצב מנוי ושולחת התראה. היא אינה מוחקת דבר, ואינה סוגרת חוב — חוב נסגר
          בתשלום שאומת מול הספק, ולא בעדכון אמצעי תשלום.
        </p>
      </InfoNote>

      {outcome === null ? null : outcome.kind === 'failed' ? (
        <ActionFailureNotice failure={outcome.failure} onStepUpSatisfied={() => setOutcome(null)} />
      ) : (
        <ReviewResult tenantUid={outcome.tenantUid} review={outcome.review} />
      )}

      <ServerRead result={arrears.result} whatHe="את החובות הפתוחים" skeletonRows={6}>
        {(report) => (
          <>
            <PolicyPanel report={report} />
            <ArrearsPanel report={report} onReview={setReviewing} />

            {/*
              THE EDITOR LAST. The question an operator opens this screen with is *"who has not
              paid"*; the thresholds are a setting they change rarely, and putting a form above the
              list would make the rare case the first thing on the page. 09 §1.4.
            */}
            <ServerRead result={policies.result} whatHe="את זמני הגבייה" skeletonRows={3}>
              {(known) => (
                <PolicyEditor
                  report={known}
                  onRecorded={() => {
                    policies.reload();
                    /*
                     * AND THE ARREARS TABLE TOO. The stage of every office is computed from the
                     * governing policy, so a new row dated today changes every row above --
                     * leaving the table as it was would show stages judged by thresholds that are
                     * no longer in force.
                     */
                    arrears.reload();
                  }}
                />
              )}
            </ServerRead>

            {reviewing === null ? null : (
              <Modal
                intentHe="בדיקת גבייה עכשיו"
                /*
                 * 09 §2.2 — the blast radius names what will happen AND what will not, and it is
                 * computed from the row being acted on rather than from a sentence written once:
                 * a review of an office at `current` moves nothing, and telling the operator it
                 * will suspend somebody would be a promise the server does not keep.
                 */
                blastRadiusHe={blastRadiusFor(reviewing)}
                fields={0}
                confirmLabelHe={sending ? 'בודק…' : 'בדיקה עכשיו'}
                onConfirm={() => void review(reviewing)}
                onClose={() => setReviewing(null)}
              >
                {/*
                  NO FIELDS, AND NO `irreversible`. A review is a READ OF A CLOCK followed by the
                  state that clock already demands -- and the next nightly pass performs exactly
                  the same act whether or not this button is pressed. Asking for a typed reason
                  would be asking a person to justify agreeing with the arithmetic; marking it
                  irreversible would be a stronger claim than the act deserves, and 09 §2.2 keeps
                  that re-selection for the acts that genuinely cannot be undone.
                */}
                <p className="form__note">
                  הבדיקה מריצה עכשיו את מה שהסריקה הלילית תריץ בכל מקרה.
                </p>
              </Modal>
            )}
          </>
        )}
      </ServerRead>
    </AppShell>
  );
}

/**
 * WHAT A REVIEW OF THIS ROW WILL DO, in the row's own terms.
 *
 * `demands` is the server's answer to *"what does this stage require of the subscription"*, and
 * `null` is its most important value: a cycle at `current` demands NOTHING, not `active`. An office
 * can be `trialing`, or `cancelled` and running out a paid period, and a review must not promote
 * it — so the sentence for that case promises no state change at all.
 */
function blastRadiusFor(line: ArrearsLine): string {
  const notice = 'אם מגיע מועד התראה לשלב הזה, תישלח התראה לאיש הקשר לחיוב. ';
  const nothingElse = 'הפעולה אינה גובה כסף, אינה מפיקה מסמך, אינה סוגרת את החוב ואינה מוחקת דבר.';
  return line.demands === null
    ? `השלב הנוכחי אינו דורש שינוי מצב מנוי, ולכן מצב המנוי לא ישתנה. ${notice}${nothingElse}`
    : `מצב המנוי של ${line.legalName} יועבר ל־${stateHe(line.demands)} אם הוא אינו שם כבר. ` +
        `${notice}${nothingElse}`;
}

/** The subscription states a dunning stage can demand. */
function stateHe(state: string): string {
  if (state === 'past_due') return 'פיגור בתשלום';
  if (state === 'suspended') return 'מושעה';
  if (state === 'expired') return 'פג';
  /*
   * RENDERED AS IT CAME. A default of "מושעה" would print a suspension about a state nobody
   * recognised — the same rule `billingOutcomeHe` follows for an unknown outcome word.
   */
  return state;
}

function PolicyPanel({ report }: { readonly report: ArrearsReport }): JSX.Element {
  if (report.policy === null) {
    return (
      <StateBlock
        titleHe="אין מדיניות גבייה בתוקף"
        bodyHe={
          'לא קיימת שורת מדיניות שתקפה להיום, ולכן אף חוב אינו מתקדם בשלבים והסריקה דוחה כל ' +
          'ארגון בנימוק „אין מדיניות בתוקף”. הטבלה שלמטה מציגה את החובות הפתוחים, וכולם ייקראו ' +
          '„תקין” כל עוד אין זמנים מוגדרים. יש לקבוע את הזמנים לפני שהגבייה יכולה לפעול.'
        }
        /*
         * `unavailable` AND NOT `error`. The estate is not broken: nobody has set the day counts,
         * which is a configuration state with something to do about it. `StateBlock`'s four tones
         * are the product's, and there is no `warning` among them -- a fifth invented here would
         * be a tone no stylesheet knows.
         */
        tone="unavailable"
      />
    );
  }

  const policy = report.policy;
  return (
    <section className="notice" data-tone="info" data-area="dunning-policy">
      <h2 className="notice__title">{`הזמנים שבתוקף — מ־${policy.effectiveFrom}`}</h2>
      <p className="notice__body">
        {`התראה ${afterDaysHe(policy.warnAfterDays)} · הסדרה בלבד ${afterDaysHe(policy.restrictAfterDays)} · ` +
          `הרשמה מחדש ${afterDaysHe(policy.terminateAfterDays)} · התראה חוזרת כל ${daysHe(policy.noticeIntervalDays)}.`}
      </p>
      <p className="notice__body">
        {`נכון ל־${report.on}. השורה מתוארכת: שינוי נרשם כשורה חדשה מיום שנבחר, והחובות שנשפטו ` +
          'לפי הזמנים הקודמים נשפטו לפיהם.'}
      </p>
    </section>
  );
}

function ArrearsPanel({
  report,
  onReview,
}: {
  readonly report: ArrearsReport;
  readonly onReview: (line: ArrearsLine) => void;
}): JSX.Element {
  if (report.lines.length === 0) {
    return (
      <StateBlock
        titleHe="אין חובות פתוחים"
        bodyHe={`נכון ל־${report.on} אין אף ארגון עם חיוב שנכשל ולא הוסדר.`}
        tone="empty"
      />
    );
  }

  return (
    <section className="notice" data-tone="warning" data-area="arrears">
      <h2 className="notice__title">{`חובות פתוחים — ${report.lines.length}`}</h2>
      <div className="grid">
        <div className="grid__scroll">
          <table className="grid__table">
            <caption className="grid__caption">
              {`מסודר לפי מועד הכשל הראשון, הוותיק למעלה — אותו סדר שהסריקה פועלת בו`}
            </caption>
            <thead className="grid__head">
              <tr>
                <th scope="col">לקוח</th>
                <th scope="col">כשל ראשון</th>
                <th scope="col">ימים</th>
                <th scope="col">שלב</th>
                <th scope="col">ניסיונות</th>
                <th scope="col">סיבת הכשל האחרון</th>
                <th scope="col">פעולה</th>
              </tr>
            </thead>
            <tbody>
              {report.lines.map((line) => (
                <tr key={line.tenantUid}>
                  <td>
                    <a href={routes.platformTenant(line.tenantUid)}>{line.legalName}</a>
                  </td>
                  <td>
                    <span dir="ltr">{line.firstFailedOn}</span>
                  </td>
                  <td>
                    {/* A SERVER FIGURE. `null` is a date the domain could not count from, and it
                        is shown as missing rather than as zero days of arrears. */}
                    {line.daysUnsettled === null ? '—' : line.daysUnsettled}
                  </td>
                  <td>
                    <StageChip stage={line.stage} governed={report.policy !== null} />
                  </td>
                  <td>{line.attemptCount}</td>
                  <td>
                    {/* THE PROVIDER'S OWN CODE, UNTRANSLATED AND MARKED AS THEIRS. A map from
                        their codes to Hebrew would be a second vocabulary that goes stale the
                        first time they add one, and `null` is a real case: a cycle opened by a
                        failure with no code. */}
                    {line.lastFailureCode === null ? (
                      <span>לא נמסרה סיבה</span>
                    ) : (
                      <span dir="ltr">{line.lastFailureCode}</span>
                    )}
                  </td>
                  <td>
                    <button type="button" className="btn" onClick={() => onReview(line)}>
                      בדיקה עכשיו
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/**
 * The stage, in the product's own words.
 *
 * `paymentStageHe` is the SAME function the customer card and the office's own statement use, so
 * the operator and the customer are told the same thing in the same words about the same state.
 */
function StageChip({
  stage,
  governed,
}: {
  readonly stage: DunningStage;
  readonly governed: boolean;
}): JSX.Element {
  const label = paymentStageHe(stage, governed);
  return <StatusChip labelHe={label.labelHe} tone={label.tone} />;
}

function ReviewResult({
  tenantUid,
  review,
}: {
  readonly tenantUid: string;
  readonly review: DunningReviewRead;
}): JSX.Element {
  /*
   * THREE INDEPENDENT ANSWERS, REPORTED SEPARATELY. The service returns the state change and the
   * notice outcome as separate fields precisely because they fail independently: the subscription
   * can move while the mail fails, and one word for both would hide whichever half went wrong.
   */
  return (
    <section className="notice" data-tone="info" data-area="review-result">
      <h2 className="notice__title">נבדק</h2>
      <p className="notice__body">
        {review.refusal === null
          ? `השלב שנמצא: ${paymentStageHe(review.stage, true).labelHe}.`
          : `הבדיקה לא הושלמה: ${refusalHe(review.refusal)}.`}
      </p>
      <p className="notice__body">
        {review.stateChangedTo === null
          ? 'מצב המנוי לא שונה — או שהוא כבר במצב שהשלב דורש, או שהשלב אינו דורש מצב.'
          : `מצב המנוי הועבר ל־${stateHe(review.stateChangedTo)}.`}
      </p>
      <p className="notice__body">{noticeHe(review.notice)}</p>
      <p className="notice__body">
        <a href={routes.platformTenant(tenantUid)}>מעבר לכרטיס הלקוח</a>
      </p>
    </section>
  );
}

/** Why a review could not run. Four named reasons, each with something different to do about it. */
function refusalHe(refusal: string): string {
  if (refusal === 'tenant_not_found') return 'הארגון לא נמצא';
  if (refusal === 'subscription_not_found') return 'לארגון אין מנוי פעיל';
  if (refusal === 'no_governing_policy') return 'אין מדיניות גבייה בתוקף להיום';
  if (refusal === 'no_billing_contact')
    return 'אין איש קשר לחיוב, ולכן לא ניתן לשלוח את ההתראה. מצב המנוי כן עודכן';
  return refusal;
}

/** What became of the notice. `suppressed` is neither a delivery nor a failure, and says so. */
function noticeHe(notice: DunningReviewRead['notice']): string {
  if (notice.kind === 'sent') return 'ההתראה נשלחה.';
  if (notice.kind === 'already_raised') return 'התראה לשלב הזה כבר נשלחה, ולכן לא נשלחה שוב.';
  if (notice.kind === 'suppressed')
    return 'ההתראה נרשמה ולא יצאה — הערוץ בשרת הזה אינו שולח דואר. זו אינה שליחה ואינה כשל.';
  if (notice.kind === 'failed') return `שליחת ההתראה נכשלה: ${notice.reason ?? 'לא נמסרה סיבה'}.`;
  if (notice.kind === 'not_due')
    return `לא הגיע מועד התראה: ${notice.reason ?? 'לא נמסרה סיבה'}.`;
  return `מצב ההתראה: ${notice.kind}.`;
}

/**
 * THE DAY COUNTS, SET FORWARD.
 *
 *     *"המערכת תתקדם לפי הזמנים שהוגדרו"*
 *
 * ── IT IS AN INSERT AND THERE IS NO EDIT, WHICH THE FORM HAS TO SAY ───────────────────────────
 *
 * The store has no update method, deliberately: a cycle judged under last month's thresholds was
 * judged by them, and an edited row cannot say so. So this form always asks for a DATE, and a day
 * that already has a policy is refused rather than overwritten --
 * *"אל תדרוס הגדרות קיימות ואל תיצור כפילויות."*
 *
 * ── THE FIELDS ARE PRE-FILLED FROM THE GOVERNING ROW, NOT FROM DEFAULTS ───────────────────────
 *
 * An operator changing one threshold must not have to retype the other three from memory, and a
 * form pre-filled with numbers THIS FILE chose would offer an estate a policy nobody set. When no
 * row governs, the fields are EMPTY and the form says why: there is nothing to copy from, and
 * inventing 14 and 30 here would be this screen deciding the Owner’s policy.
 */
function PolicyEditor({
  report,
  onRecorded,
}: {
  readonly report: DunningPolicyReport;
  readonly onRecorded: () => void;
}): JSX.Element {
  const governing = report.governing;
  const [from, setFrom] = useState('');
  const [warn, setWarn] = useState(governing === null ? '' : String(governing.warnAfterDays));
  const [restrict, setRestrict] = useState(
    governing === null ? '' : String(governing.restrictAfterDays),
  );
  const [terminate, setTerminate] = useState(
    governing === null ? '' : String(governing.terminateAfterDays),
  );
  const [interval, setInterval] = useState(
    governing === null ? '' : String(governing.noticeIntervalDays),
  );
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<
    { readonly kind: 'done' } | { readonly kind: 'failed'; readonly failure: ActionFailure } | null
  >(null);

  /*
   * WHOLE DAYS, AND THE CHECK IS ON THE PARSE RATHER THAN ON THE TEXT. `Number('14.5')` is a
   * number and 14.5 days is not a threshold -- the column is an INT and a fractional value would
   * be truncated into a policy nobody set. The route refuses one too; this is so the button is
   * disabled rather than the request refused.
   */
  const day = (text: string): number | null => {
    const n = Number(text);
    return text.trim() !== '' && Number.isInteger(n) && n >= 0 ? n : null;
  };
  const w = day(warn);
  const r = day(restrict);
  const t2 = day(terminate);
  const i = day(interval);
  const dated = /^\d{4}-\d{2}-\d{2}$/u.test(from);
  /*
   * THE ORDER IS CHECKED HERE TOO, AND IT IS A MIRROR THAT SAYS SO. `CK_dunning_policy_order` is
   * what makes a contradictory policy unstorable and the service refuses it by name; this exists
   * only to disable the button, so an operator sees the problem beside the field instead of after
   * a round trip. If this were deleted the estate would still refuse the row.
   */
  const ordered = w !== null && r !== null && t2 !== null && w < r && r < t2;
  const ready = dated && ordered && i !== null && i >= 1;

  async function record(): Promise<void> {
    if (!ready || w === null || r === null || t2 === null || i === null) return;
    setSending(true);
    try {
      await callOperation('platform.dunning.policy.set', {
        body: {
          effectiveFrom: from,
          warnAfterDays: w,
          restrictAfterDays: r,
          terminateAfterDays: t2,
          noticeIntervalDays: i,
        },
      });
      setResult({ kind: 'done' });
      onRecorded();
    } catch (err) {
      setResult({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      setConfirming(false);
    }
  }

  return (
    <section className="notice" data-tone="info" data-area="policy-editor">
      <h2 className="notice__title">קביעת זמני הגבייה</h2>
      <p className="notice__body">
        {'שינוי נרשם כשורה חדשה מיום שנבחר, ואינו משנה שורה קיימת. חוב שנשפט לפי הזמנים הקודמים ' +
          'נשפט לפיהם, ולכן אין מסך לעריכת שורה שנרשמה.'}
      </p>

      {governing === null ? (
        <p className="notice__body">
          אין שורה בתוקף, ולכן השדות ריקים. המסך אינו ממלא מספרים מעצמו.
        </p>
      ) : null}

      {result === null ? null : result.kind === 'failed' ? (
        <ActionFailureNotice failure={result.failure} onStepUpSatisfied={() => setResult(null)} />
      ) : (
        <p className="notice__body">הזמנים נרשמו.</p>
      )}

      <div className="field-group">
        <label className="field">
          <span className="field__label">בתוקף מיום</span>
          <input
            className="field__input"
            name="dunning-effective-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.currentTarget.value)}
          />
          <span className="field__hint">היום שממנו הזמנים החדשים שופטים חוב.</span>
        </label>
        <label className="field">
          <span className="field__label">התראה אחרי (ימים)</span>
          <input
            className="field__input"
            name="dunning-warn"
            inputMode="numeric"
            value={warn}
            onChange={(e) => setWarn(e.currentTarget.value)}
          />
          <span className="field__hint">העבודה נמשכת כרגיל.</span>
        </label>
        <label className="field">
          <span className="field__label">הסדרה בלבד אחרי (ימים)</span>
          <input
            className="field__input"
            name="dunning-restrict"
            inputMode="numeric"
            value={restrict}
            onChange={(e) => setRestrict(e.currentTarget.value)}
          />
          <span className="field__hint">המנוי מושעה והנתונים נשמרים.</span>
        </label>
        <label className="field">
          <span className="field__label">הרשמה מחדש אחרי (ימים)</span>
          <input
            className="field__input"
            name="dunning-terminate"
            inputMode="numeric"
            value={terminate}
            onChange={(e) => setTerminate(e.currentTarget.value)}
          />
          <span className="field__hint">בלי שחזור אוטומטי ובלי ניסיון חיוב חדש אוטומטי.</span>
        </label>
        <label className="field">
          <span className="field__label">התראה חוזרת כל (ימים)</span>
          <input
            className="field__input"
            name="dunning-interval"
            inputMode="numeric"
            value={interval}
            onChange={(e) => setInterval(e.currentTarget.value)}
          />
          <span className="field__hint">לפחות יום אחד.</span>
        </label>
      </div>

      {/*
        ── BOTH OF THESE WERE INVERTED, AND ONLY THE RENDERED SCREEN SAID SO. **2026-11-03.** ──

        They read `ready || !dated ? null : …`, which shows the *"choose a date"* note exactly when
        a date HAS been chosen and hides it when one has not -- and showed *"the order must
        increase"* beside four fields whose order was already increasing, because the form was not
        `ready` for the unrelated reason that the date was blank.

        A dom case asserting the notes are PRESENT would have passed on both. What found it was
        opening the screen and reading it, which is the same way the Hebrew plural defect was
        found a day earlier. Each note now answers only its own question.
      */}
      {dated ? null : <p className="form__note">יש לבחור יום תוקף.</p>}
      {ordered ? null : (
        <p className="form__note">
          סדר הזמנים חייב להיות עולה: התראה, אחר כך הסדרה בלבד, ואחר כך הרשמה מחדש.
        </p>
      )}
      {i === null || i >= 1 ? null : (
        <p className="form__note">התראה חוזרת חייבת להיות לפחות יום אחד.</p>
      )}

      <div className="actions-row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={!ready}
          onClick={() => setConfirming(true)}
        >
          רשימת הזמנים
        </button>
      </div>

      {!confirming ? null : (
        <Modal
          intentHe="רשימת זמני גבייה"
          blastRadiusHe={
            `מיום ${from} יישפט כל חוב פתוח לפי ${String(w)} / ${String(r)} / ${String(t2)} ימים, ` +
            `והתראה תישלח כל ${String(i)} ימים. השינוי חל גם על חובות שנפתחו כבר, ` +
            'משום שהשלב מחושב מיום הכשל הראשון ולא נשמר כסטטוס. הפעולה אינה משנה שורה קיימת, ' +
            'אינה משנה מצב מנוי עכשיו ואינה שולחת התראה. הסריקה הבאה תפעל לפי הזמנים החדשים.'
          }
          fields={0}
          confirmLabelHe={sending ? 'רושם…' : 'רשימה'}
          onConfirm={() => void record()}
          onClose={() => setConfirming(false)}
        >
          <p className="form__note">
            אם כבר קיימת שורה ליום הזה, הבקשה תידחה ולא תדרוס אותה.
          </p>
        </Modal>
      )}

      {report.policies.length === 0 ? null : (
        <table>
          <caption>כל השורות שנרשמו, הוותיקה למעלה</caption>
          <thead>
            <tr>
              <th scope="col">בתוקף מיום</th>
              <th scope="col">התראה</th>
              <th scope="col">הסדרה בלבד</th>
              <th scope="col">הרשמה מחדש</th>
              <th scope="col">התראה חוזרת</th>
            </tr>
          </thead>
          <tbody>
            {report.policies.map((row) => (
              <tr key={row.effectiveFrom}>
                <td>
                  <span dir="ltr">{row.effectiveFrom}</span>
                  {row.effectiveFrom === governing?.effectiveFrom ? ' — בתוקף' : null}
                </td>
                <td>{row.warnAfterDays}</td>
                <td>{row.restrictAfterDays}</td>
                <td>{row.terminateAfterDays}</td>
                <td>{row.noticeIntervalDays}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * A THRESHOLD IN DAYS, READ ALOUD. **Seen in the browser, 2026-11-03.**
 *
 * The seeded policy warns after ZERO days -- which is correct and means immediately -- and the
 * sentence rendered *"התראה אחרי 0 ימים"*, which reads as a number nobody set rather than as a
 * policy. One and two have their own forms in Hebrew for the same reason the customer count did.
 */
function afterDaysHe(days: number): string {
  if (days === 0) return 'מיד';
  return `אחרי ${daysHe(days)}`;
}

function daysHe(days: number): string {
  if (days === 1) return 'יום אחד';
  if (days === 2) return 'יומיים';
  return `${days} ימים`;
}
